import { shiftKeys, updateShiftType } from "@/api/Shifts";
import { Trans, useLingui } from "@lingui/react/macro";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import styles from "./ShiftDetails.module.scss";
import Form from "@/components/Form/Form";
import { Loading } from "@medoma/components/Loading/Loading";
import Select from "@medoma/components/Select/Select";
import { FilledButton, PlainButton } from "@medoma/components/Button/Button";
import { useState } from "react";
import { deducedError, useErrorMessageAlert } from "@/Utils/ErrorUtils";
import {
  shiftTypeSchema,
  shiftTypesDictionary,
  type IScheduledShift,
  type IShiftType,
} from "@medoma/models/shifts";

const NO_SHIFT_TYPE = "";

export const ChangeShiftType = ({
  shift,
  shiftTypes,
}: {
  shift: IScheduledShift;
  shiftTypes: IShiftType[];
}) => {
  const { t } = useLingui();

  // Display "No Type" if the shift type is Ambulating since the sites do not use this type
  const isShiftTypeAmbulating = shift.type === shiftTypeSchema.enum.Ambulating;
  const shiftId = shift.id;

  const { register, handleSubmit } = useForm<{
    scheduledType: IShiftType | typeof NO_SHIFT_TYPE;
  }>({
    defaultValues: {
      scheduledType: isShiftTypeAmbulating ? NO_SHIFT_TYPE : shift.type,
    },
  });

  const [isChangingShiftType, setIsChangingShiftType] = useState(false);
  const queryClient = useQueryClient();
  const { displayErrorMessageAlert } = useErrorMessageAlert();

  const {
    mutate: mutateShiftType,
    isPending: isMutatingShiftType,
    variables: mutationVariables,
  } = useMutation({
    mutationFn: ({
      shiftType,
      shiftId,
    }: {
      shiftType: IShiftType | typeof NO_SHIFT_TYPE;
      shiftId: IScheduledShift["id"];
    }) => {
      if (shiftType === NO_SHIFT_TYPE) {
        return updateShiftType({ shiftId, shiftType: null });
      }
      return updateShiftType({ shiftId, shiftType });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: shiftKeys.all });
      setIsChangingShiftType(false);
    },
    onError: (error) => {
      displayErrorMessageAlert(
        `${t`Det gick inte att genomföra ändringen`}. ${deducedError(error)}`,
      );
    },
  });

  const loadingMessage = isMutatingShiftType
    ? mutationVariables?.shiftType === NO_SHIFT_TYPE
      ? t`Tar bort typ`
      : t`Byter typ`
    : null;

  return (
    <>
      <div className={styles.actions}>
        {isChangingShiftType ? (
          <PlainButton
            size="small"
            onClick={() => {
              setIsChangingShiftType(false);
            }}
            disabled={isMutatingShiftType}
            aria-label={t`Avbryt ändring av typ`}
          >
            <Trans>Avbryt</Trans>
          </PlainButton>
        ) : (
          <PlainButton
            size="small"
            onClick={() => {
              setIsChangingShiftType(true);
            }}
            disabled={isMutatingShiftType}
            aria-label={
              isShiftTypeAmbulating
                ? t`Lägg till typ av arbetspass`
                : t`Ändra typ av arbetspass`
            }
          >
            {isShiftTypeAmbulating ? (
              <Trans>Lägg till</Trans>
            ) : (
              <Trans>Ändra</Trans>
            )}
          </PlainButton>
        )}
      </div>
      {isChangingShiftType ? (
        <div className={styles.editContainer}>
          <Form
            onSubmit={handleSubmit(async (validatedFormData) => {
              mutateShiftType({
                shiftType: validatedFormData.scheduledType,
                shiftId,
              });
            })}
          >
            {loadingMessage ? (
              <Loading message={loadingMessage} />
            ) : (
              <>
                <Select
                  label={t`Välj typ av arbetspass`}
                  {...register("scheduledType")}
                >
                  <option value={NO_SHIFT_TYPE}>
                    <Trans>Ingen</Trans>
                  </option>
                  {shiftTypes.map((type) => {
                    return (
                      <option value={type} key={type}>
                        {t(shiftTypesDictionary[type])}
                      </option>
                    );
                  })}
                </Select>
                <FilledButton disabled={isMutatingShiftType} type="submit">
                  <Trans>Spara ändringar</Trans>
                </FilledButton>
              </>
            )}
          </Form>
        </div>
      ) : null}
    </>
  );
};
